import { onBeforeUnmount, onMounted, readonly, ref, type Ref } from 'vue';

/**
 * Reactive flag for whether the Option/Alt key is currently held down.
 *
 * Lets controls offer an Option-modifier — e.g. the play buttons turn into a
 * "play HLS" affordance while Option is held and stream the HLS version instead
 * of the MP3. Kept module-level so every consumer shares one reactive flag and
 * one set of window listeners (ref-counted), and so the state is consistent
 * across the cover-art buttons and the global player at the same instant.
 */
const altHeld = ref(false);
let consumers = 0;

// `altKey` is the macOS Option key; reading it off every keydown/keyup keeps the
// flag correct even when Option is chorded with other keys.
function syncFromEvent(event: KeyboardEvent): void {
    altHeld.value = event.altKey;
}

// The window can lose focus while Option is still down (e.g. Option+Tab), so the
// keyup never arrives — clear the flag on blur so it never sticks.
function reset(): void {
    altHeld.value = false;
}

export function useAltKeyHeld(): Readonly<Ref<boolean>> {
    onMounted(() => {
        if (consumers === 0) {
            window.addEventListener('keydown', syncFromEvent);
            window.addEventListener('keyup', syncFromEvent);
            window.addEventListener('blur', reset);
        }
        consumers += 1;
    });

    onBeforeUnmount(() => {
        consumers -= 1;
        if (consumers === 0) {
            window.removeEventListener('keydown', syncFromEvent);
            window.removeEventListener('keyup', syncFromEvent);
            window.removeEventListener('blur', reset);
        }
    });

    return readonly(altHeld);
}
